/**
 * Credit Edit Modal Component
 * 
 * Modal for adjusting user credits with proper tracking and validation
 */

import React, { useState } from 'react';
import { X, CreditCard, Plus, Minus, AlertCircle } from 'lucide-react';

interface User {
  id: string;
  name: string;
  email: string;
  credits: number;
}

interface Props {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onCreditUpdate: (userId: string, creditData: any) => Promise<void>;
}

export default function CreditEditModal({ user, isOpen, onClose, onCreditUpdate }: Props) {
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'deduct' | 'set'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const amountNum = parseInt(amount);
    
    // Validation
    if (!amountNum || amountNum <= 0) {
      setError('Amount must be a positive number');
      return;
    }

    if (!reason.trim()) {
      setError('Reason is required for credit adjustments');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onCreditUpdate(user.id, {
        type: adjustmentType,
        amount: amountNum,
        reason: reason.trim()
      });

      // Reset form
      setAmount('');
      setReason('');
      setAdjustmentType('add');
    } catch (error: any) {
      console.error('Failed to update credits:', error);
      setError(error.response?.data?.message || error.message || 'Failed to update credits');
    } finally {
      setLoading(false);
    }
  };

  const calculateNewBalance = () => {
    const amountNum = parseInt(amount) || 0;
    const currentCredits = user.credits || 0;

    switch (adjustmentType) {
      case 'add':
        return currentCredits + amountNum;
      case 'deduct':
        return Math.max(0, currentCredits - amountNum); // Don't go below 0
      case 'set':
        return amountNum;
      default:
        return currentCredits;
    }
  };

  const handleClose = () => {
    setAmount('');
    setReason('');
    setAdjustmentType('add');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Edit Credits</h2>
              <p className="text-sm text-gray-500">{user.name}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Current Credits */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Current Credits:</span>
              <span className="text-lg font-bold text-green-600">
                {user.credits?.toLocaleString() || 0}
              </span>
            </div>
          </div>

          {/* Adjustment Type */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Adjustment Type</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setAdjustmentType('add')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border rounded-lg font-medium transition-colors ${
                  adjustmentType === 'add'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Plus className="w-4 h-4" />
                Add Credits
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentType('deduct')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border rounded-lg font-medium transition-colors ${
                  adjustmentType === 'deduct'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Minus className="w-4 h-4" />
                Deduct Credits
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentType('set')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border rounded-lg font-medium transition-colors ${
                  adjustmentType === 'set'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Set Amount
              </button>
            </div>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {adjustmentType === 'set' ? 'New Credit Amount' : 'Credit Amount'}
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount..."
              min="1"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain the reason for this credit adjustment..."
              rows={3}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* New Balance Preview */}
          {amount && (
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-700">New Balance:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {user.credits?.toLocaleString() || 0}
                  </span>
                  <span className="text-blue-600">→</span>
                  <span className="text-lg font-bold text-blue-700">
                    {calculateNewBalance().toLocaleString()}
                  </span>
                </div>
              </div>
              {adjustmentType === 'deduct' && calculateNewBalance() === 0 && parseInt(amount) > (user.credits || 0) && (
                <div className="flex items-center gap-2 mt-2 text-yellow-700">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Credits will be set to 0 (cannot go negative)</span>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !amount || !reason.trim()}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating...
                </div>
              ) : (
                'Update Credits'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}